import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLoyaltyOffersPopupOpener } from "../../../services/hooks/loyalty/useLoyaltyOffersPopupOpener";
import { usePayWithPoints } from "../../../services/hooks/loyalty/usePayWithPoints";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useDineInMenuView } from "../../../services/hooks/useDineInMenuView";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import {
  selectCartItemsNumber,
  selectCartSubtotal,
  selectCartTotal,
} from "../../../services/redux/selectors/cartSelectors";
import { selectSingleScrollHomeScreen } from "../../../services/redux/selectors/homeScreenSelectors";
import { selectLoyaltyFlashOffers } from "../../../services/redux/selectors/loyaltyOfferSelectors";
import {
  selectGuestTiersEnabled,
  selectIsVerifiedOrganizationCustomer,
  selectUseLoyaltyService,
} from "../../../services/redux/selectors/loyaltyServiceSelectors";
import { redeemOfferAction } from "../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  setBottomSheetState,
  setGlobalSnackBarState,
  setMenuView,
  updateCurrentHomeScreenTab,
} from "../../../services/redux/slices/globalComponentsToggleSlice";
import { setLayoutProps } from "../../../services/redux/slices/LayoutSlice";
import { getDownloadAppPopupDetails } from "../../../utils/APICalls/APICalls";
import { shouldShowDownloadAppPopup } from "../../../utils/CookieHelper";
import { PopupStage } from "../../../utils/promoMaterial/promoMaterialTypes";
import { usePromoPopups } from "../../../services/context/PromoPopupContext";
import {
  COOKIE_ENUM,
  CUSTOMER_ANALYTICS_EVENT,
  ContainerStyle,
  HeaderEnum,
  LocalStorageEnum,
  MENU_STYLE,
  PagesEnum,
  RESTAURANT_IMAGE_TYPE,
  TabsEnum,
  URL_PARAMS,
} from "../../../utils/enums";
import env from "../../../utils/env.config";
import {
  checkAvailability,
  getAllDisabledItems,
} from "../../../utils/help/itemDetails/itemDetails";
import { levenshteinDistance } from "../../../utils/help/maths/maths";
import { rankMenuItems } from "../../../utils/help/menuSearch/menuSearch";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import { allFreeItemsAndWithMinSpendZeroV2 } from "../../../utils/help/offers/offers";
import { isVisitor } from "../../../utils/help/user/user";
import LS from "../../../utils/LocalStorage";
import {
  LoyaltyOfferSystem,
  LoyaltyTierSystemTier,
} from "../../../utils/loyaltyTypes";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { createOffer } from "../../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import {
  CategoryType,
  INVERTED_SECTIONS,
  OFFER_SYSTEM,
  RestaurantTypeEnum,
} from "../../../utils/types";
import { DeliverySelectionDrawerV2 } from "../../global/DeliverySelectionDrawer/DeliverySelectionDrawerV2/DeliverySelectionDrawerV2";
import FlashOfferBanner from "../../global/FlashOfferBanner/FlashOfferBanner";
import { GeneralOfferIcon } from "../../global/GeneralOfferIcon/GeneralOfferIcon";
import { FranchiseSelector } from "../../global/LocationsDropdown/FranchiseSelector";
import LoyaltyFlashOfferBanner from "../../global/LoyaltyFlashOfferBanner/LoyaltyFlashOfferBanner";
import { ReferAFriend } from "../../global/ReferAFriend/ReferAFriend";
import { TermsAndConditionsSection } from "../../global/TermsAndConditionsSection/TermsAndConditionsSection";
import { useAuth } from "../../Layout/auth/useAuth";
import BottomSheet from "../../shared/BottomSheet/BottomSheet";
import SelectOrderInfo from "../../shared/SelectOrderInfo/SelectOrderInfo";
import { TextInput } from "../../shared/TextInput/TextInput";
import { SliderComponent } from "../../slider/Slider.component";
import { BAR_ID, CategoryBarV2 } from "./CategoryBarV2/CategoryBarV2";
import { CategoryListV2 } from "./CategoryItemList/CategoryListV2";
import styles from "./CategoryScreen.module.scss";
import { OrderTypeIcon } from "./Icons/icons";
import { ItemView } from "./Icons/ItemView.icons";
import { ListView } from "./Icons/ListView.icons";
import { NoResultsV2 } from "./Icons/NoResults";
import { LoyaltyPointsLoyaltySection } from "./LoyaltyPointsLoyaltySection/LoyaltyPointsLoyaltySection";
import { LoyaltyPointsMenuSection } from "./LoyaltyPointsMenuSection/LoyaltyPointsMenuSection";
import { OrganizationCustomerOfferComponent } from "./OrganizationCustomerOffer/OrganizationCustomerOfferComponent";
import { PreviousOrdersList } from "./PreviousOrderList/PreviousOrdersList";
import { PreviousOrderTab } from "./PreviousOrderList/PreviousOrderTab/PreviousOrderTabComponent";
import { PromoBannerRail } from "../../global/PromoBanner/PromoBannerRail";
import { RestaurantBannerSlider } from "./RestaurantBannerSlider/RestaurantBannerSlider.component";
import { HomeGreetingRow } from "./HomeGreetingRow/HomeGreetingRow";
import { MenuSearchBar } from "./MenuSearchBar/MenuSearchBar";
import SelectOrderBanner from "./SelectOrderBanner/SelectOrderBanner";
import { selectAcceptedTermsAndConditions } from "../../../services/redux/selectors/termsAndConditionsSelectors";

const SEARCH_RESULTS_CATEGORY_ID = "search-results";

// The bar is sticky, so its rect reports the stuck offset rather than its
// place in the flow, and an offsetTop walk misses any padding on the body.
// Dropping the stickiness for the length of one synchronous read gives the
// real position without either correction.
const flowTop = (element: HTMLElement) => {
  const position = element.style.position;

  element.style.position = "static";

  const top = element.getBoundingClientRect().top + window.scrollY;

  element.style.position = position;

  return top;
};

// Neither scrollend nor a duration is dependable here, so watch for the target
// itself. Stillness would count the gap before the animation starts as arrival.
const whenScrollReaches = (target: number, done: () => void) => {
  const deadline = performance.now() + 1200;

  let frame = requestAnimationFrame(function tick() {
    const furthest = document.documentElement.scrollHeight - window.innerHeight;

    if (
      Math.abs(window.scrollY - Math.min(target, furthest)) <= 2 ||
      performance.now() > deadline
    ) {
      done();

      return;
    }

    frame = requestAnimationFrame(tick);
  });

  return () => cancelAnimationFrame(frame);
};

/** Session flag so the view-only notice greets each scan once, not every render. */
const VIEW_ONLY_SNACKBAR_SHOWN_KEY = "dineInMenuViewSnackbarShown";

export const CategoryScreen = (props: {
  allCategories: CategoryType[];
  rerenderFlag: number;
  areaAboveNavBar: number | undefined;
}) => {
  const authApp = useAuth();
  const { t } = useTranslationI18();
  const isMobile = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isMobile
  );
  const referralCode = useAppSelector((state) => state.userInfo.referralCode);
  const isTermsAndConditions = useAppSelector(selectAcceptedTermsAndConditions);
  const addCustomerAnalytics = useCustomerAnalytics();
  const { handleModal } = useModalHook();
  const { askForStage } = usePromoPopups();
  const { openLoyaltyOffersPopup } = useLoyaltyOffersPopupOpener();
  const dispatch = useAppDispatch();
  const LayoutProps: any = useAppSelector((state) => state.layout.layoutProps);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  /**
   * Static dine-in menu (prepits/project#4291): the whole loyalty/offer layer of
   * the home screen stands down, along with the tab bar, the cart and the
   * pop-ups.
   *
   * Note it is deliberately NOT folded into `useLoyaltyBanners` or
   * `useLoyaltyPopups` below. Those two pick which implementation owns a slot,
   * not whether the slot is filled: forcing `useLoyaltyBanners` false handed the
   * top-banner slot back to the legacy RestaurantBannerSlider the rail had
   * replaced, so a restaurant on the rail grew an empty banner block it has
   * never shown. Suppress what renders instead, at each render site.
   */
  const dineInMenuView = useDineInMenuView();
  const useLoyaltyPopups = restaurantConfig?.useLoyaltyPopups === true;
  const useLoyaltyBanners = restaurantConfig?.useLoyaltyBanners === true;
  /** One home ask per visit — see the effect below. */
  const askedForHomePopup = useRef(false);

  const restaurantType = useAppSelector(
    (state) => state.config.restaurantInfo?.type
  );
  const isEcommerce: boolean = restaurantType === RestaurantTypeEnum.ECOMMERCE;
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const guestTiersEnabled = useAppSelector(selectGuestTiersEnabled);
  const isPayWithPointsEnabled = usePayWithPoints("browse").isEnabled;

  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );
  const addressDrawerState = useAppSelector(
    (state) => state.globalComponentsToggleSlice.isOpenAddressSelection
  );
  const hasWelcomeDrawerV3 = useAppSelector(
    (state) => state.config.restaurantInfo?.config?.hasWelcomeDrawerV3
  );

  const isFranchiseOpen = useAppSelector(
    (store) => store.franchiseStatus.currentIsOpen
  );
  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartLoyaltyV2Reducer = useAppSelector(
    (state) => state.cartLoyaltyV2Reducer
  );
  const loyaltyTierSystem = useAppSelector(
    (state) => state.loyaltyTierSystem.data
  );
  const loyaltyCustomerInfo = useAppSelector(
    (state) => state.loyaltyCustomerInfo.data
  );
  const organizationCustomer = useAppSelector(
    (state) => state.partnerAccount.organizationCustomer
  );
  const isOrganizationCustomer = useAppSelector(
    selectIsVerifiedOrganizationCustomer
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);

  const modalRedux = useAppSelector((state) => state.modal.modal);

  const offersSliderData = useMemo(
    () =>
      cartAndLoyaltyReducer.offersArray.filter(
        (offer) => offer.offerSystem !== OFFER_SYSTEM.POINTING_OFFER
      ),
    [cartAndLoyaltyReducer.offersArray]
  );

  const loyaltyOffersSliderData = useAppSelector(selectLoyaltyFlashOffers);

  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );

  const cart = cartLoyaltyV2Reducer.cart;
  const cartItemsNumber = useAppSelector(selectCartItemsNumber);
  const cartSubtotal = useAppSelector(selectCartSubtotal);
  const cartTotal = useAppSelector(selectCartTotal);

  const cartData = {
    cartItemsNumber,
    totalBill: cartSubtotal,
    totalPrice: cartTotal,
  };

  const redeemedOffer = cartAndLoyaltyReducer.offersArray.find((o) =>
    o.isEqual(cartLoyaltyV2Reducer.cart.redeemedOffer)
  );

  const [user, setUser] = useState("");
  const [change, setChange] = useState(true);
  const [disabledItemsIds, setDisabledItemsIds] = useState<number[]>([]);
  const selectedTab = useAppSelector(
    (state) => state.globalComponentsToggleSlice.currentHomeScreenTab
  );
  const singleScrollHome = useAppSelector(selectSingleScrollHomeScreen);
  // The tab bar is gone under the static dine-in menu, so a Rewards/My Orders
  // tab left over in the persisted state would strand the guest on an empty
  // screen with no way back - the same reason single-scroll pins it to the menu.
  const activeTab =
    singleScrollHome || dineInMenuView ? TabsEnum.Menu : selectedTab;
  const phoneNumberInfo = useAppSelector(
    (state) => state.userInfo.phoneNumberInfo
  );
  const username = useAppSelector((state) => state.userInfo.username);
  const router = useRouter();

  const menuView = useAppSelector(
    (state) => state.globalComponentsToggleSlice.menuView
  );

  const view = [
    { title: MENU_STYLE.LIST, icon: <ListView /> },
    { title: MENU_STYLE.ITEM, icon: <ItemView /> },
  ];
  const [menuCategories, setFilteredCategories] = useState(
    null as CategoryType[] | null
  );
  const searchWrapperBarRef = useRef<HTMLDivElement>(null);
  const [searchWrapperBarHeight, setSearchWrapperBarHeight] = useState(0);
  const maxRewardsPageScrollDepthRef = useRef(0);

  useEffect(() => {
    if (activeTab !== TabsEnum.Menu) {
      return;
    }
    const el = searchWrapperBarRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSearchWrapperBarHeight(entry.target.getBoundingClientRect().height);
      }
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === TabsEnum.Loyalty) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.VIEW_REWARDS);
    }
  }, [activeTab]);

  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchValue), 200);

    return () => clearTimeout(timeout);
  }, [searchValue]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRegionRef = useRef<HTMLDivElement>(null);

  const closeSearch = () => {
    setSearchValue("");
    setSearchOpen(false);
  };

  // Opening is one movement: the field widens, the menu fades and the page
  // travels to the pin together. The fill below is reserved before the scroll
  // starts, so the menu dropping out mid-flight cannot shorten the page under
  // it.
  useEffect(() => {
    if (!singleScrollHome || !searchOpen) {
      return;
    }

    let stopWatching: (() => void) | undefined;

    // Measures where the row will pin and reserves exactly the space under it,
    // returning the scroll that gets it there. The fill goes straight onto the
    // node rather than through state, so the re-align below is not clamped by a
    // height React has not committed yet.
    const alignToPin = () => {
      const bar = document.getElementById(BAR_ID);
      const region = searchRegionRef.current;

      if (!bar || !region) {
        return null;
      }

      const stickyTop = parseFloat(getComputedStyle(bar).top) || 0;
      const barTop = flowTop(bar);
      const regionTop = region.getBoundingClientRect().top + window.scrollY;
      const regionOffset = regionTop - barTop + stickyTop;

      region.style.setProperty("--search-region-top", `${regionOffset}px`);

      return barTop - stickyTop;
    };

    const target = alignToPin();

    if (target !== null) {
      window.scrollTo({ top: target, behavior: "smooth" });
      stopWatching = whenScrollReaches(target, () => {
        // The nav can finish settling to a different height while the page is
        // travelling, which moves the pin out from under the row. Re-measure
        // once it has landed and close the remainder.
        const settled = alignToPin();

        if (settled !== null && Math.abs(settled - window.scrollY) > 1) {
          window.scrollTo({ top: settled });
        }
      });
    }

    return () => {
      stopWatching?.();
    };
  }, [singleScrollHome, searchOpen]);

  useEffect(() => {
    if (!singleScrollHome || !searchOpen || searchValue) {
      return;
    }

    const collapseOnOutsideTap = (event: MouseEvent | TouchEvent) => {
      if (!document.getElementById(BAR_ID)?.contains(event.target as Node)) {
        closeSearch();
      }
    };

    document.addEventListener("mousedown", collapseOnOutsideTap);
    document.addEventListener("touchstart", collapseOnOutsideTap);

    return () => {
      document.removeEventListener("mousedown", collapseOnOutsideTap);
      document.removeEventListener("touchstart", collapseOnOutsideTap);
    };
  }, [singleScrollHome, searchOpen, searchValue]);

  const offersSoon = t("offers:offers-soon");
  const stayTuned = t("offers:stay-tuned");
  const searchText = t("common:search");
  const showingResultsForText = t("common:showing-results-for");
  const noResultsFoundText = t("common:no-results-found");

  const customerLoyaltyTier =
    useLoyaltyService &&
    loyaltyTierSystem?.tiers &&
    (!isVisitor()
      ? loyaltyTierSystem?.tiers.find(
          (tier: LoyaltyTierSystemTier) =>
            tier.tierId === loyaltyCustomerInfo?.tierProgress?.currentTierId
        )
      : loyaltyTierSystem?.tiers[0]);

  // ----------------------Pay with Points is a menu entry point of its own: it needs no tiers and no milestones-----------------------
  const showPointingSystem =
    !dineInMenuView &&
    (guestTiersEnabled ||
      isPayWithPointsEnabled ||
      (customerLoyaltyTier && customerLoyaltyTier?.milestones
        ? customerLoyaltyTier?.milestones?.length > 0
        : cartAndLoyaltyReducer.pointingSystem));

  const showReferralCodeBanner =
    !dineInMenuView &&
    referralCode.rewardedPoints > 0 &&
    !referralCode.promoCode.expired;

  const showFlashOfferBanners =
    !dineInMenuView &&
    offersSliderData &&
    !restaurantInfo?.config?.disableOffersSliderInHomeScreen &&
    offersSliderData.length !== 0;

  const showLoyaltyFlashOfferBanners =
    !dineInMenuView &&
    useLoyaltyService &&
    !!loyaltyOffersSliderData?.length &&
    !restaurantInfo?.config?.disableOffersSliderInHomeScreen;

  const showOrganizationCustomerOfferBanner =
    !dineInMenuView &&
    isOrganizationCustomer &&
    activeTab === TabsEnum.Menu &&
    (useLoyaltyService
      ? loyaltyOffersSliderData?.every(
          (offer) => offer.offerSystem === LoyaltyOfferSystem.PARTNERSHIP
        )
      : offersSliderData.length > 0);

  const showSelectOrderDataBanner = !selectedFranchise || !orderTypeParam;

  const searchResults = useMemo(() => {
    if (!debouncedSearch || !menuCategories?.length) {
      return [];
    }

    const ranked = rankMenuItems(menuCategories, debouncedSearch);

    return ranked.length
      ? [
          {
            ...menuCategories[0],
            id: SEARCH_RESULTS_CATEGORY_ID,
            itemCategories: ranked.map((result) => result.itemCategory),
          },
        ]
      : [];
  }, [menuCategories, debouncedSearch]);

  const isOrderDataLoading = useAppSelector(
    (state) => state.globalComponentsToggleSlice.orderDataLoading
  );
  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  if (change) {
    if (authApp.ReturnAuthenticatedUser()) {
      setUser(authApp.ReturnAuthenticatedUser()?.name);
    }
    setChange(false);
  }

  const checkVerifyMailLogic = () => {
    const verifyEmailModalCookie = Cookies.get(
      COOKIE_ENUM.VERIFY_EMAIL_POPUP_LAST_APPEARED
    );

    if (
      // !useLoyaltyService &&
      organizationCustomer &&
      !organizationCustomer.verified &&
      !verifyEmailModalCookie
    ) {
      handleModal(true, "VerifyEmail/VerifyEmailModal", {
        componentProps: {
          nextPage: PagesEnum.Cart,
        },
      });
    } else {
      router.push(`${getPageLink(router, PagesEnum.Cart).as}`);
    }
  };

  const handleDownloadAppPopup = async () => {
    // Check if feature is enabled
    if (!restaurantConfig?.showDownloadAppPopup) {
      return false;
    }

    // Get interval from config, default to 12 hours
    const intervalHours =
      restaurantConfig?.showDownloadAppPopup?.downloadAppPopupInterval || 12;

    const restaurantId = Number(env.restaurantId) ?? restaurantInfo?.id;

    // Check if popup should be shown based on timing
    const canShow = shouldShowDownloadAppPopup(intervalHours);
    if (!canShow) {
      return false;
    }

    try {
      // Fetch popup details.
      const response = await getDownloadAppPopupDetails(restaurantId);
      const popupData = response.data;

      // Backend returns:
      // - null                          -> customer already downloaded the app
      // - { showPopup: true }           -> show popup without offer
      // - { showPopup: true, offer: {} } -> show popup with offer
      if (!popupData || !popupData.showPopup) {
        return false;
      }

      const downloadAppPopupProps: {
        screenshotImage: string;
        restaurantId: number;
        offer?: Offer;
      } = {
        screenshotImage:
          restaurantConfig?.showDownloadAppPopup?.downloadAppPopupImage,
        restaurantId,
      };

      const offerData = popupData.offer;

      if (offerData) {
        downloadAppPopupProps.offer = createOffer(offerData as any);
      }

      handleModal(true, "DownloadAppPopup/DownloadAppPopup", {
        componentProps: downloadAppPopupProps,
      });

      return true;
    } catch (error) {
      console.error("Failed to fetch download app popup details:", error);

      return false;
    }
  };

  const maybeOpenOffersPopup = (options?: { ignoreSeenCookie?: boolean }) => {
    try {
      const offersPopupCookie = Cookies.get(COOKIE_ENUM.OFFER_MODAL);

      if (
        offersSliderData.length > 0 &&
        !modalRedux?.isOpen &&
        selectedFranchise &&
        orderTypeParam &&
        !isOrderDataLoading
      ) {
        if (
          offersSliderData.length === 1 &&
          offersSliderData[0].isMultiplierOffer() &&
          !redeemedOffer
        ) {
          if (
            offersSliderData[0].isOfferApplicable({
              timeZone: restaurantInfo?.timeZone!,
              orderType: orderTypeParam,
              itemStatusRedux,
              cart: cartLoyaltyV2Reducer.cart,
            })
          ) {
            dispatch(redeemOfferAction({ offer: offersSliderData[0] }));
          }
        } else if (!offersPopupCookie || options?.ignoreSeenCookie) {
          // Cookie present = customer already saw the offers popup: nothing
          // to open. (This whole block is gated on no modal being open, so
          // the old handleModal(false) here could never close anything —
          // but its dispatch raced modal opens from other flows, e.g. the
          // BOGO redeem redirect opening ItemFromList.)
          // ignoreSeenCookie: OffersPopup marks itself seen on MOUNT, so a
          // popup that was displaced by another modal after a brief flash
          // still poisons the cookie — the caller can override.
          openLoyaltyOffersPopup({ isFromHomeScreen: true });
        }
      }
    } catch (error) {
      console.error("Error in handlePopups:", error);
    }
  };

  useEffect(() => {
    // Nothing on this screen is actionable under the static dine-in menu, so no
    // offer, reward or download-app pop-up has anywhere to send the guest.
    if (dineInMenuView) {
      return;
    }

    if (!isTermsAndConditions || isOrganizationCustomer) {
      handleModal(false, "", {});

      return;
    }

    // A REWARD directive names its offer by uuid, so asking before the offer
    // list has landed resolves nothing and falls back to plain authored copy —
    // which is still *recorded*, spending the session's slot so the real redeem
    // dialog never appears. PromoPopupSignInWatcher waits for the same reason.
    if (useLoyaltyPopups && isOrderDataLoading) {
      return;
    }

    const handlePopups = async () => {
      try {
        // Story #4087: for a migrated restaurant the server decides what belongs
        // on the home screen — which pop-up, in which order, and whether the
        // customer has already had it. The ladder below is the pre-migration
        // path and stays until every restaurant has flipped the flag; the two
        // must never both run, or a customer gets two pop-ups.
        if (useLoyaltyPopups) {
          // Once per visit. `inFlight` in the provider only rejects *concurrent*
          // asks, and this effect re-runs on `router`, on the order-type banner
          // and on `isOrderDataLoading` settling — each of which would otherwise
          // be a fresh round trip, with a fire-and-forget display report racing
          // the next GET.
          if (askedForHomePopup.current) {
            return;
          }
          askedForHomePopup.current = true;
          await askForStage(PopupStage.HOME);

          return;
        }

        // Trigger download app popup with API data
        const openedDownloadAppPopup = await handleDownloadAppPopup();

        if (openedDownloadAppPopup) {
          return;
        }

        maybeOpenOffersPopup();
      } catch (error) {
        console.error("Error in handlePopups:", error);
      }
    };

    // Call handlePopups immediately
    handlePopups();
  }, [
    restaurantConfig,
    selectedFranchise?.franchiseConfig,
    isOrganizationCustomer,
    offersSliderData.length,
    router,
    isTermsAndConditions,
    isOrderDataLoading,
    showSelectOrderDataBanner,
    dineInMenuView,
  ]);

  // Re-check the offers popup on the modal open->closed edge only (e.g. the
  // referral modal closing should let the welcome popup open). With React 18
  // batching, the main effect's deps can all settle while another modal is
  // still up, so without this the popup would never open. Kept out of the
  // main effect: depending on modal state there would also fire the
  // terms-guard close on every modal OPEN, killing just-opened popups.
  const wasModalOpenRef = useRef(false);
  const lastOpenModalComponentRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const wasOpen = wasModalOpenRef.current;
    const closedModalComponent = lastOpenModalComponentRef.current;
    wasModalOpenRef.current = !!modalRedux?.isOpen;
    if (modalRedux?.isOpen) {
      // component is in the deps so a modal->modal replacement (e.g. the
      // referral modal displacing an offers popup) updates this too.
      // (typed ReactNode in the slice; only the string names matter here)
      lastOpenModalComponentRef.current =
        typeof modalRedux.component === "string"
          ? modalRedux.component
          : undefined;
    }

    if (!wasOpen || modalRedux?.isOpen) {
      return;
    }
    if (!isTermsAndConditions || isOrganizationCustomer) {
      return;
    }

    maybeOpenOffersPopup({
      // A referral link must always land on the welcome/offers popup after
      // the referral modal closes. The popup may have already flashed open
      // and been displaced by the referral modal — its mount set the "seen"
      // cookie, which would otherwise veto this reopen (CI trace: promo-code
      // spec, welcome-popup-button timeout).
      ignoreSeenCookie: closedModalComponent === "ReferralModal/ReferralModal",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalRedux?.isOpen, modalRedux?.component]);

  useLayoutEffect(() => {
    const cartFooterProps = {
      text: `${t("footer:view-cart")}`,
      price: cartData.totalBill,
      click: () => checkVerifyMailLogic(),
      link: null,
      priceWithoutVat: true,
      priceLabel: `${t("footer:global-subtotal")}`,
      display: JSON.parse(
        (cart &&
          cartData.cartItemsNumber > 0 &&
          isMobile &&
          (cartData.totalPrice > 0 ||
            allFreeItemsAndWithMinSpendZeroV2(
              cartAndLoyaltyReducer,
              cartLoyaltyV2Reducer
            )) &&
          isFranchiseOpen &&
          isTermsAndConditions) + ""
      ),
    };

    const shouldOpenBottomSheet =
      restaurantInfo && (!selectedFranchise || !orderTypeParam);

    const franchiseFooterProps = {
      text: `${t("common:select-branch")}`,
      click: () => dispatch(setBottomSheetState(true)),
      link: null,
      price: null,
      display: isMobile,
    };

    dispatch(
      setLayoutProps({
        ...LayoutProps,
        headerProps: {
          restaurantInfo: restaurantInfo,
          components: dineInMenuView
            ? [HeaderEnum.Menu, HeaderEnum.Logo]
            : [HeaderEnum.Menu, HeaderEnum.Logo, HeaderEnum.Cart],
          backLink: null,
          currentPage: PagesEnum.Category,
          dynamicPagination: null,
          display: true,
          testId: "cart",
        },
        footerProps: dineInMenuView
          ? { ...cartFooterProps, display: false }
          : shouldOpenBottomSheet
          ? franchiseFooterProps
          : cartFooterProps,
      })
    );
  }, [
    isFranchiseOpen,
    isTermsAndConditions,
    cartData.totalBill,
    cartData.cartItemsNumber,
    dineInMenuView,
  ]);

  useEffect(() => {
    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.VIEW_MENU);
    if (!singleScrollHome && LS._get(LocalStorageEnum.ACTIVE_VIEW)) {
      dispatch(setMenuView(LS._get(LocalStorageEnum.ACTIVE_VIEW, true)));
    } else {
      dispatch(
        setMenuView(
          restaurantInfo?.config?.menuStyle === MENU_STYLE.ITEM
            ? MENU_STYLE.ITEM
            : MENU_STYLE.LIST
        )
      );
    }

    if (window.location.href.includes(URL_PARAMS.OFFERS_TAB)) {
      if (singleScrollHome) {
        router.push(getPageLink(router, PagesEnum.Rewards).as);
      } else {
        dispatch(updateCurrentHomeScreenTab(TabsEnum.Loyalty));
      }
    }
  }, []);

  /**
   * AC: "I go directly to the menu (without any drawers)". The welcome drawer
   * asks which branch and which order type - both already answered by the QR
   * link - but `useFranchiseData` opens it on mount from cookies alone, so a
   * first-time visitor got it anyway.
   *
   * Only the automatic open is suppressed. A `source` means the guest asked for
   * it (burger menu -> Order Type on a restaurant without the V3 location
   * screen), which is how they switch back to a full ordering experience, so
   * that one is left alone.
   */
  useEffect(() => {
    if (dineInMenuView && bottomSheetModal.isOpen && !bottomSheetModal.source) {
      dispatch(setBottomSheetState(false));
    }
  }, [dineInMenuView, bottomSheetModal.isOpen, bottomSheetModal.source]);

  // Tell the guest once per visit why nothing can be added. Kept in
  // sessionStorage rather than a ref so walking into an item and back doesn't
  // replay it, while a fresh scan of the QR code does.
  useEffect(() => {
    if (!dineInMenuView) {
      return;
    }

    try {
      if (window.sessionStorage.getItem(VIEW_ONLY_SNACKBAR_SHOWN_KEY)) {
        return;
      }
      window.sessionStorage.setItem(VIEW_ONLY_SNACKBAR_SHOWN_KEY, "true");
    } catch {
      // Private browsing / storage disabled: show it, just don't remember.
    }

    dispatch(
      setGlobalSnackBarState({
        isOpen: true,
        text: t("common:dine-in-view-only-menu"),
      })
    );
  }, [dineInMenuView]);

  useEffect(() => {
    if (activeTab !== TabsEnum.Loyalty) {
      return;
    }

    maxRewardsPageScrollDepthRef.current = 0;

    const trackDepth = () => {
      const maxScrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (maxScrollable <= 0) {
        return;
      }

      const depth = Math.min(
        100,
        Math.max(0, (window.scrollY / maxScrollable) * 100)
      );
      maxRewardsPageScrollDepthRef.current = Math.max(
        maxRewardsPageScrollDepthRef.current,
        depth
      );
    };

    window.addEventListener("scroll", trackDepth, { passive: true });

    return () => {
      window.removeEventListener("scroll", trackDepth);
      const depth = Math.round(maxRewardsPageScrollDepthRef.current);
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.REWARDS_PAGE_SCROLL_DEPTH, {
        additionalDetails: JSON.stringify({ depth }),
      });
      maxRewardsPageScrollDepthRef.current = 0;
    };
  }, [activeTab]);

  useEffect(() => {
    if (restaurantInfo) {
      let disabledCategories: string[] = [];
      const newDisabledItemsIds = getAllDisabledItems(
        itemStatusRedux,
        orderTypeParam
      );

      setDisabledItemsIds(newDisabledItemsIds);
      props.allCategories &&
        props.allCategories.forEach((category) => {
          const itemCategoriesIds = category.itemCategories.map(
            (obj: any) => obj.itemId
          );
          const allElementsDisabled = itemCategoriesIds.every((element) =>
            newDisabledItemsIds.includes(element)
          );
          if (allElementsDisabled) {
            disabledCategories = [...disabledCategories, category.id];
          }
        });

      const newCategories = props.allCategories.filter(
        (category) =>
          !disabledCategories.includes(category.id) &&
          (!restaurantInfo || category.restaurantId === restaurantInfo.id)
      );

      // Without the flag the menu search still works by narrowing this list in
      // place. Under it the ranked results below do that job, and narrowing here
      // too would flatten the menu on every keystroke.
      const menuCategories = singleScrollHome
        ? newCategories
        : filterCategoriesBySearch(newCategories);

      const menuCategoriesWithTimeBound = menuCategories?.map(
        (filteredCategory) => {
          const categoryTimeBoundAvailability =
            filteredCategory.categoryTimeBoundAvailabilities?.[0];

          const categoryTimeBoundAvailabilityData = checkAvailability(
            categoryTimeBoundAvailability?.startTime,
            categoryTimeBoundAvailability?.endTime,
            categoryTimeBoundAvailability?.enabled,
            restaurantInfo.timeZone,
            t
          );

          const itemCategoriesWithTimeBound =
            filteredCategory.itemCategories.map((itemCategory) => {
              const itemTimeBoundAvailability =
                itemCategory.item.itemTimeBoundAvailabilities?.[0];

              const itemTimeBoundAvailabilityData = checkAvailability(
                itemTimeBoundAvailability?.startTime,
                itemTimeBoundAvailability?.endTime,
                itemTimeBoundAvailability?.enabled,
                restaurantInfo.timeZone,
                t
              );

              const isItemAvailable =
                !categoryTimeBoundAvailabilityData?.isAvailable
                  ? false
                  : itemTimeBoundAvailabilityData.isAvailable;

              const availabilityMessage =
                categoryTimeBoundAvailabilityData?.availabilityMessage ||
                itemTimeBoundAvailabilityData.availabilityMessage;

              const fullMessage =
                categoryTimeBoundAvailabilityData?.fullMessage ||
                itemTimeBoundAvailabilityData.fullMessage;

              return {
                ...itemCategory,
                item: {
                  ...itemCategory.item,
                  timeBoundAvailabilityData: {
                    isAvailable: isItemAvailable,
                    availabilityMessage,
                    fullMessage,
                  },
                },
              };
            });

          return {
            ...filteredCategory,
            timeBoundAvailabilityData: categoryTimeBoundAvailabilityData,
            itemCategories: itemCategoriesWithTimeBound,
          };
        }
      );

      const availableCategories = menuCategoriesWithTimeBound?.filter(
        (category) => category.timeBoundAvailabilityData.isAvailable
      );
      const unavailableCategories = menuCategoriesWithTimeBound?.filter(
        (category) => !category.timeBoundAvailabilityData.isAvailable
      );

      const sortedCategoriesByTimeBound = [
        ...(availableCategories ?? []),
        ...(unavailableCategories ?? []),
      ];

      setFilteredCategories(sortedCategoriesByTimeBound as CategoryType[]);
    }
  }, [
    searchValue,
    singleScrollHome,
    itemStatusRedux,
    props.allCategories,
    restaurantInfo?.id,
  ]);

  function filterCategoriesBySearch(categories: CategoryType[] | null) {
    if (!searchValue) {
      return categories;
    }

    const queryTokens = searchValue.toLowerCase().split(" ");
    const searchedCategories: CategoryType[] = [];

    categories?.forEach((category) => {
      const itemCategories = category.itemCategories.filter((itemCategory) => {
        let name = itemCategory.item.item_name.toLowerCase();

        if (itemCategory.item.nameLocalized) {
          name += ` ${itemCategory.item.nameLocalized.toLowerCase()}`;
        }

        if (name.includes(searchValue.toLocaleLowerCase())) {
          return true;
        }

        const itemTokens = name.toLowerCase().split(" ");

        return queryTokens.some((queryToken) =>
          itemTokens.some(
            (itemToken) =>
              levenshteinDistance(queryToken, itemToken) < itemToken.length / 2
          )
        );
      });

      if (itemCategories.length > 0) {
        searchedCategories.push({ ...category, itemCategories });
      }
    });

    return searchedCategories;
  }

  // ----------------------Held as values so pay-with-points can put the balance card first without the markup being written twice-----------------------
  /**
   * Story #4087: with `useLoyaltyBanners` on, the loyalty service serves this
   * whole carousel — the offers slider here and the top restaurant-image slider
   * below are both replaced by this one rail, in the server's order. It takes the
   * same slot rather than a new one, so pay-with-points keeps ordering it.
   *
   * Hidden rather than unmounted, so the fetched banners survive a tab switch —
   * and for the order-type state, which is a guard the replaced slider carried:
   * its own slider is showing, and an offer cannot be applied before an order
   * type exists anyway.
   */
  const promoBannerRail =
    useLoyaltyBanners && !dineInMenuView ? (
      <PromoBannerRail
        key="menu-offers-slider"
        hidden={activeTab !== TabsEnum.Menu || showSelectOrderDataBanner}
        tierSized={guestTiersEnabled}
        trailing={
          /* The order-type slider already ends with this one — rendering it here
           too would show the referral banner twice. */
          showReferralCodeBanner && !showSelectOrderDataBanner ? (
            <ReferAFriend containerStyle={ContainerStyle.BOXED} />
          ) : undefined
        }
      />
    ) : null;

  /**
   * The organization-customer treatment is not campaign material and belongs to
   * neither flag: when a partnership offer applies it owns this section
   * outright, which is why the rail stands down for it rather than stacking.
   */
  const menuOffersSlider =
    activeTab === TabsEnum.Menu &&
    !showSelectOrderDataBanner &&
    env.featureFreeItemSlider &&
    (showLoyaltyFlashOfferBanners ||
      showFlashOfferBanners ||
      showReferralCodeBanner) &&
    showOrganizationCustomerOfferBanner ? (
      <OrganizationCustomerOfferComponent
        key="menu-offers-slider"
        offerDetails={offersSliderData}
      />
    ) : (
      promoBannerRail ??
      (activeTab === TabsEnum.Menu &&
        (showLoyaltyFlashOfferBanners ||
          showFlashOfferBanners ||
          showReferralCodeBanner) && (
          <SliderComponent key="menu-offers-slider">
            {showLoyaltyFlashOfferBanners
              ? loyaltyOffersSliderData!.map((offer) => (
                  <LoyaltyFlashOfferBanner
                    key={offer.id}
                    offer={offer}
                    tierSized={guestTiersEnabled}
                  />
                ))
              : showFlashOfferBanners &&
                offersSliderData.map((offer, index) => (
                  <FlashOfferBanner key={index} offer={offer} />
                ))}
            {showReferralCodeBanner && (
              <ReferAFriend containerStyle={ContainerStyle.BOXED} />
            )}
          </SliderComponent>
        ))
    );

  const loyaltyPointsMenuSection = activeTab === TabsEnum.Menu &&
    showPointingSystem &&
    !restaurantInfo?.config?.disablePointingInHomeScreen && (
      <div
        key="loyalty-points-menu-section"
        className={styles.loyalty_points_menu_section}
      >
        <LoyaltyPointsMenuSection />
      </div>
    );

  return (
    <div
      className={`${styles.root} ${
        LayoutProps.footerProps.display && styles.padding_bottom
      } ${isMainDark ? "bg__zero" : "bg__02"} ${
        singleScrollHome ? styles.single_scroll : ""
      }`}
    >
      {!isTermsAndConditions && <TermsAndConditionsSection />}
      {/* Under hasWelcomeDrawerV3 the delivery-selection drawer must never open
          on the home/menu: that flow uses the banner -> location screen (see
          FranchiseSelector), not this drawer. Rendering it unconditionally let a
          stray auto-open (useCustomerData, when order_type=deliveryv2 with no
          selected address) stack it under the welcome drawer after backing out
          of the location screen (prepits/project#3913).

          Non-V3 restaurants still need it here: their FranchiseSelector opens
          this drawer in place (handleChangeAddress) and useCustomerData
          auto-opens it for a deliveryv2 order type with no address yet. With
          nothing mounted to render it, both were silent no-ops on the
          home/menu. */}
      {!hasWelcomeDrawerV3 && addressDrawerState && (
        <DeliverySelectionDrawerV2 />
      )}

      {/* Figma 14893:53512: the greeting leads the scroll and the order-type
          card sits under it, the reverse of the tab-bar layout. The greeting
          also absorbs the tier badge and the way into My Orders, both of which
          the tab bar used to carry. */}
      {singleScrollHome ? (
        <>
          <HomeGreetingRow user={user} />
          {!showSelectOrderDataBanner && (
            <div className={styles.franchise_selector_wrapper}>
              <FranchiseSelector />
            </div>
          )}
        </>
      ) : (
        <>
          {!showSelectOrderDataBanner && (
            <div className={styles.franchise_selector_wrapper}>
              <FranchiseSelector />
            </div>
          )}
          {(!guestTiersEnabled ||
            (phoneNumberInfo.isValid && username) ||
            user.length > 0) && (
            <div className={`${styles.greeting_wrapper}`}>
              <h1
                data-testid="user-greeting"
                className={styles.welcome_title + " main_title "}
              >
                {(phoneNumberInfo.isValid && username) || user.length > 0 ? (
                  <>
                    <span>
                      {`${
                        isEcommerce
                          ? t("common:category-hello")
                          : t("common:category-hey")
                      }`}
                      {","}
                    </span>
                    <p className={styles.username}>
                      {
                        (username || user)
                          .toLocaleLowerCase()
                          .replace(/-/g, " ")
                          .split(" ")[0]
                      }
                    </p>
                  </>
                ) : (
                  `${t("common:category-visitor-greeting")}`
                )}
              </h1>
            </div>
          )}
        </>
      )}
      {!singleScrollHome && !dineInMenuView && (
        <PreviousOrderTab currentTab={activeTab} />
      )}

      {activeTab === TabsEnum.Menu && showSelectOrderDataBanner && (
        <div className={styles.select_order_banner_slider}>
          <SliderComponent>
            <SelectOrderBanner
              bgColor="primary"
              bannerText={t("common:price-diff-banner")}
              icon={
                <OrderTypeIcon color={restaurantInfo?.colors?.primary || ""} />
              }
            />
            <SelectOrderBanner
              bgColor="loyalty"
              bannerText={t("common:order-info-banner")}
              icon={<GeneralOfferIcon backgroundIconColor="loyalty" />}
            />
            {showReferralCodeBanner && (
              <ReferAFriend containerStyle={ContainerStyle.BOXED} />
            )}
          </SliderComponent>
        </div>
      )}
      {/* Flash offers lead the menu tab: the offer cards are what the guest
          is meant to act on, so they sit above the balance and the milestone
          meter rather than under them. The pay-with-points card is the
          exception — it is the guest's own balance and headlines the tab. */}
      {isPayWithPointsEnabled || singleScrollHome
        ? [loyaltyPointsMenuSection, menuOffersSlider]
        : [menuOffersSlider, loyaltyPointsMenuSection]}

      {activeTab === TabsEnum.Menu &&
        !useLoyaltyBanners &&
        restaurantInfo?.restaurantImages && (
          <RestaurantBannerSlider
            restaurantImages={restaurantInfo.restaurantImages?.filter(
              (images) => images.type === RESTAURANT_IMAGE_TYPE.TOP_BANNER
            )}
          />
        )}

      {activeTab === TabsEnum.PreviousOrders && <PreviousOrdersList />}
      {activeTab === TabsEnum.Menu && !singleScrollHome && (
        <div
          ref={searchWrapperBarRef}
          id="search_wrapper_bar"
          className={`${styles.search_wrapper} ${styles.sticky} ${
            isMainDark ? "bg__zero" : "bg__02"
          }`}
          style={{
            top: `${props.areaAboveNavBar}px`,
          }}
        >
          <div className={styles.input}>
            <TextInput
              required={true}
              type="text"
              placeholder={searchText}
              name="menuSearch"
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              hasClear
              onClear={() => {
                setSearchValue("");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              value={searchValue}
              testid={`menu-search-input`}
            />
          </div>

          {env.featureMenuView && !singleScrollHome && (
            <div className={styles.view_wrapper}>
              {view.map((view, index) => {
                return (
                  <button
                    key={`${view.title}-${index}`}
                    className={`${styles.view_button} ${
                      menuView === view.title && styles.active_view
                    }`}
                    onClick={() => {
                      if (menuView !== view.title) {
                        addCustomerAnalytics(
                          view.title === MENU_STYLE.ITEM
                            ? CUSTOMER_ANALYTICS_EVENT.MENU_GRID_VIEW
                            : CUSTOMER_ANALYTICS_EVENT.MENU_LIST_VIEW
                        );
                      }
                      LS._set(LocalStorageEnum.ACTIVE_VIEW, view.title, true);
                      dispatch(setMenuView(view.title));
                    }}
                    data-testid={`${view.title}-view-button`}
                  >
                    {view.icon}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
      {activeTab === TabsEnum.Menu && singleScrollHome && menuCategories && (
        <CategoryBarV2
          areaAboveNavBar={props.areaAboveNavBar}
          searchWrapperBarHeight={0}
          categories={menuCategories}
          leadingExpanded={searchOpen}
          leading={
            <MenuSearchBar
              expanded={searchOpen}
              value={searchValue}
              onChange={setSearchValue}
              onExpand={() => setSearchOpen(true)}
              onCollapse={closeSearch}
            />
          }
        />
      )}
      {activeTab === TabsEnum.Menu &&
        !singleScrollHome &&
        menuCategories &&
        (!searchValue ? (
          <CategoryBarV2
            areaAboveNavBar={props.areaAboveNavBar}
            searchWrapperBarHeight={searchWrapperBarHeight}
            categories={menuCategories}
          />
        ) : (
          <div className={styles.showing_results}>
            <p>
              {menuCategories && menuCategories.length > 0 ? (
                <>
                  {showingResultsForText}:<span>{` ${searchValue}`}</span>
                </>
              ) : (
                noResultsFoundText
              )}
            </p>
          </div>
        ))}
      {activeTab === TabsEnum.Loyalty && (
        <div className={styles.offers_tab_wrapper} data-testid={"offersPage"}>
          {showPointingSystem ? (
            <LoyaltyPointsLoyaltySection user={user} />
          ) : showLoyaltyFlashOfferBanners && !showSelectOrderDataBanner ? (
            <SliderComponent>
              {loyaltyOffersSliderData!.map((offer) => (
                <LoyaltyFlashOfferBanner key={offer.id} offer={offer} />
              ))}
            </SliderComponent>
          ) : offersSliderData &&
            offersSliderData.length !== 0 &&
            !showSelectOrderDataBanner ? (
            <SliderComponent>
              {offersSliderData.map((offer, index) => (
                <FlashOfferBanner key={index} offer={offer} offersTab={true} />
              ))}
            </SliderComponent>
          ) : (
            <div
              id="offer_wrapper"
              className={`default_offer_banner ${styles.default_offer_banner_offers_tab}`}
              data-testid={`offer-banner`}
            >
              <p>{`${offersSoon}`}</p>
              <p>{`${stayTuned}`}</p>
            </div>
          )}
        </div>
      )}
      {activeTab === TabsEnum.Menu && !singleScrollHome && (
        <CategoryListV2
          categories={menuCategories}
          disabledItemsIds={disabledItemsIds}
          logoUrl={restaurantInfo?.logoUrl || ""}
        />
      )}
      {activeTab === TabsEnum.Menu && singleScrollHome && (
        <div
          ref={searchRegionRef}
          className={searchOpen ? styles.search_region : undefined}
        >
          <div
            className={`${styles.menu_layer} ${
              searchOpen ? styles.search_hidden : ""
            }`}
          >
            <CategoryListV2
              categories={menuCategories}
              disabledItemsIds={disabledItemsIds}
              logoUrl={restaurantInfo?.logoUrl || ""}
            />
          </div>
          {!!debouncedSearch &&
            (searchResults.length ? (
              <CategoryListV2
                categories={searchResults as CategoryType[]}
                disabledItemsIds={disabledItemsIds}
                logoUrl={restaurantInfo?.logoUrl || ""}
                flat
                restoreScrollPosition={false}
              />
            ) : (
              <div className={styles.no_results}>
                <NoResultsV2 />
              </div>
            ))}
        </div>
      )}

      {activeTab === TabsEnum.Menu &&
        !searchOpen &&
        restaurantInfo?.restaurantImages && (
          <RestaurantBannerSlider
            restaurantImages={restaurantInfo.restaurantImages?.filter(
              (images) => images.type === RESTAURANT_IMAGE_TYPE.BOTTOM_BANNER
            )}
          />
        )}

      {restaurantInfo &&
        bottomSheetModal.isOpen &&
        !(dineInMenuView && !bottomSheetModal.source) && (
          <BottomSheet displayedSection={INVERTED_SECTIONS.SELECT_ORDER_INFO}>
            <SelectOrderInfo restaurantInfo={restaurantInfo} />
          </BottomSheet>
        )}
    </div>
  );
};
